/**
 * Pure, React-free rules for gating the Truth Social *sound notification*.
 *
 * Trump posts to Truth Social in large batches (retweets, "Image or video
 * posted" stubs, rapid bursts). These rules let a user suppress the beep for
 * noise while still showing every story in the feed. Kept free of React so the
 * outside-React ingest path (`ingestFirehoseStory.ts`) can import them.
 *
 * `TrumpTruthStory` only carries `title`, so retweet / media detection is
 * string-based on the title.
 */

export type TruthSocialNotificationSettings = {
  /** Beep for the first `burstCount` truths, then go quiet for `cooldownMinutes`. */
  throttleEnabled: boolean;
  burstCount: number;
  cooldownMinutes: number;
  /** When enabled, only truths whose title matches a keyword beep. */
  keywordFilterEnabled: boolean;
  keywords: string[];
  /** Skip retweets ("RT @…"). */
  skipRetweets: boolean;
  /** Skip image/video-only posts ("Image or video posted. Click to view"). */
  skipMediaOnly: boolean;
};

/**
 * All filters off → identical to today's behaviour (every truth beeps when the
 * master Truth Social sound toggle is on).
 */
export const DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS: TruthSocialNotificationSettings =
  {
    throttleEnabled: false,
    burstCount: 2,
    cooldownMinutes: 5,
    keywordFilterEnabled: false,
    keywords: [],
    skipRetweets: false,
    skipMediaOnly: false,
  };

/** Titles like "RT @realDonaldTrump …". */
export const isRetweet = (title: string): boolean => /^\s*RT\s+@/i.test(title);

/** Titles like "Image or video posted. Click to view". */
export const isMediaOnly = (title: string): boolean =>
  title.trim().toLowerCase().startsWith("image or video posted");

export const matchesKeywords = (
  title: string,
  keywords: string[]
): boolean => {
  const lowerTitle = title.toLowerCase();
  return keywords.some((k) => {
    const term = k.trim().toLowerCase();
    return term !== "" && lowerTitle.includes(term);
  });
};

/**
 * Applies the content-type skip and keyword-allowlist rules. Returns `false`
 * when the truth should be silenced. The burst throttle is handled separately
 * in the ingest module because it mutates timestamp state — and must only be
 * consumed by truths that pass these rules.
 */
export const passesTruthSocialContentRules = (
  title: string,
  settings: TruthSocialNotificationSettings
): boolean => {
  if (settings.skipRetweets && isRetweet(title)) return false;
  if (settings.skipMediaOnly && isMediaOnly(title)) return false;

  const activeKeywords = settings.keywords.filter((k) => k.trim() !== "");
  if (settings.keywordFilterEnabled && activeKeywords.length > 0) {
    if (!matchesKeywords(title, activeKeywords)) return false;
  }

  return true;
};
